// components/Compliance/EmployeeGroupEditor.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Briefcase, Save } from 'lucide-react';
import { PageHeader, AppButton } from '../ui';
import { getGroup, createGroup, updateGroup } from '../../utils/complianceApi';
import './_compliance.scss';

const EMPTY = {
  name: '',
  description: '',
  type: 'static',
  memberIds: [],
  filter: { departments: [], jobRoles: [], branchIds: [] },
};

const csv = (arr) => (arr || []).join(', ');
const fromCsv = (str) =>
  String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export default function EmployeeGroupEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await getGroup(id);
        setForm({ ...EMPTY, ...(data?.group || {}), filter: { ...EMPTY.filter, ...(data?.group?.filter || {}) } });
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateFilter = (patch) => setForm((f) => ({ ...f, filter: { ...f.filter, ...patch } }));

  const onSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        name: form.name.trim(),
        description: form.description,
        type: form.type,
        memberIds: form.type === 'static' ? form.memberIds : [],
        filter: form.type === 'dynamic' ? form.filter : undefined,
      };
      if (isNew) await createGroup(body);
      else await updateGroup(id, body);
      navigate('/compliance/employee-groups');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="cp-page">Loading…</div>;

  return (
    <div className="cp-page">
      <PageHeader
        title={isNew ? 'New Employee Group' : 'Edit Employee Group'}
        icon={Briefcase}
        backButton
        onBack={() => navigate('/compliance/employee-groups')}
        actions={
          <AppButton variant="default" onClick={onSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save'}
          </AppButton>
        }
      />

      <div className="cp-form">
        <div className="cp-field">
          <label className="cp-field__label">Name</label>
          <input className="cp-input" value={form.name} onChange={(e) => update({ name: e.target.value })} />
        </div>

        <div className="cp-field">
          <label className="cp-field__label">Description</label>
          <textarea className="cp-textarea" value={form.description} onChange={(e) => update({ description: e.target.value })} />
        </div>

        <div className="cp-field">
          <label className="cp-field__label">Type</label>
          <select className="cp-select" value={form.type} onChange={(e) => update({ type: e.target.value })}>
            <option value="static">Static — pick specific employees</option>
            <option value="dynamic">Dynamic — match by department / job role / branch</option>
          </select>
          <div className="cp-field__hint">
            Dynamic groups recompute their members each time a campaign is activated.
          </div>
        </div>

        {form.type === 'dynamic' && (
          <>
            <div className="cp-row">
              <div className="cp-field">
                <label className="cp-field__label">Departments (comma separated)</label>
                <input
                  className="cp-input"
                  value={csv(form.filter.departments)}
                  onChange={(e) => updateFilter({ departments: fromCsv(e.target.value) })}
                  placeholder="Engineering, Sales"
                />
              </div>
              <div className="cp-field">
                <label className="cp-field__label">Job Roles (comma separated)</label>
                <input
                  className="cp-input"
                  value={csv(form.filter.jobRoles)}
                  onChange={(e) => updateFilter({ jobRoles: fromCsv(e.target.value) })}
                  placeholder="Manager, Engineer"
                />
              </div>
            </div>
            <div className="cp-field">
              <label className="cp-field__label">Branch IDs (comma separated)</label>
              <input
                className="cp-input"
                value={csv(form.filter.branchIds)}
                onChange={(e) => updateFilter({ branchIds: fromCsv(e.target.value) })}
                placeholder="Optional — restrict to specific branches"
              />
            </div>
          </>
        )}

        {form.type === 'static' && (
          <div className="cp-field">
            <label className="cp-field__label">Member IDs (comma separated student IDs)</label>
            <textarea
              className="cp-textarea"
              value={csv(form.memberIds)}
              onChange={(e) => update({ memberIds: fromCsv(e.target.value) })}
              placeholder="60d…, 60d…"
            />
            <div className="cp-field__hint">
              {form.memberIds.length} employee{form.memberIds.length === 1 ? '' : 's'} selected.
            </div>
          </div>
        )}

        {error && <div className="cp-field__hint" role="alert">{error}</div>}

        <div className="cp-actions">
          <AppButton variant="outline" onClick={() => navigate('/compliance/employee-groups')} disabled={saving}>Cancel</AppButton>
          <AppButton variant="default" onClick={onSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Group'}
          </AppButton>
        </div>
      </div>
    </div>
  );
}
